/**
 * Topics service API calls.
 *
 * This module provides functions for topic cluster management including
 * fetching ranked topics, topic details, and generating new topics.
 */

import apiClient from './api';

/**
 * Get ranked topic clusters for dashboard
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Maximum number of topics to return (default: 12, max: 50)
 * @param {boolean} params.unread_only - Whether to score topics based only on unread articles
 * @returns {Promise<Object>} Ranked topics response
 */
export const getTopics = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      limit: params.limit || 12,
      unread_only: params.unread_only !== undefined ? params.unread_only : true,
    });

    const response = await apiClient.get(`/api/topics/?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch topics'
    );
  }
};

/**
 * Get detailed information about a specific topic
 * @param {string} topicId - Topic ID (UUID)
 * @param {Object} params - Query parameters
 * @param {boolean} params.unread_only - Whether to show only unread articles
 * @param {number} params.limit - Maximum number of articles to return (default: 20)
 * @param {number} params.offset - Number of articles to skip (default: 0)
 * @returns {Promise<Object>} Topic details with articles
 */
export const getTopicDetails = async (topicId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      unread_only: params.unread_only !== undefined ? params.unread_only : false,
      limit: params.limit || 20,
      offset: params.offset || 0,
    });

    const response = await apiClient.get(`/api/topics/${topicId}?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch topic details'
    );
  }
};

/**
 * Generate new topic clusters for user's articles
 * @param {Object} params - Generation parameters
 * @param {boolean} params.unread_only - Whether to generate topics for unread articles only
 * @param {boolean} params.force_regenerate - Force regeneration even if topics exist
 * @returns {Promise<Object>} Generation result
 */
export const generateTopics = async (params = {}) => {
  try {
    const requestData = {
      unread_only: params.unread_only !== undefined ? params.unread_only : true,
      force_regenerate: params.force_regenerate || false,
    };

    const response = await apiClient.post('/api/topics/generate', requestData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to generate topics'
    );
  }
};

/**
 * Delete a specific topic cluster
 * @param {string} topicId - Topic ID (UUID) to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteTopic = async (topicId) => {
  try {
    const response = await apiClient.delete(`/api/topics/${topicId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete topic'
    );
  }
};

/**
 * Delete all topic clusters for current user
 * @returns {Promise<Object>} Deletion result
 */
export const deleteAllTopics = async () => {
  try {
    const response = await apiClient.delete('/api/topics/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete all topics'
    );
  }
};

/**
 * Get or generate AI digest for a specific topic
 * @param {string} topicId - Topic ID (UUID)
 * @param {Object} params - Query parameters
 * @param {boolean} params.force - Force regeneration even if up-to-date digest exists
 * @returns {Promise<Object>} Topic digest with what's new, core theme, and recommended reads
 */
export const getTopicDigest = async (topicId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      force: params.force || false,
    });

    const response = await apiClient.get(`/api/topics/${topicId}/digest?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get topic digest'
    );
  }
};

/**
 * Mark specified articles in a topic as read.
 * @param {string} topicId - Topic ID (UUID)
 * @param {number[]} articleIds - Article IDs to mark (max 100)
 * @returns {Promise<Object>} Mark all read result
 */
export const markTopicArticlesRead = async (topicId, articleIds) => {
  try {
    const response = await apiClient.post(
      `/api/topics/${topicId}/mark-all-read`,
      { article_ids: articleIds }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to mark articles as read'
    );
  }
};

/**
 * Get the user's reading briefing - consolidated topic digests for quick browsing
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Maximum number of topic summaries to return (default: 10, max: 20)
 * @param {boolean} params.include_misc - Whether to include the Miscellaneous topic
 * @returns {Promise<Object>} Briefing response with topic digests
 */
export const getBriefing = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      limit: params.limit || 10,
      include_misc: params.include_misc !== undefined ? params.include_misc : true,
    });

    const response = await apiClient.get(`/api/topics/briefing?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch briefing'
    );
  }
};

export const getTopicDigestConditional = async (topicId, { etag } = {}) => {
  try {
    const config = {
      validateStatus: s => s === 304 || (s >= 200 && s < 300),
      ...(etag && { headers: { 'If-None-Match': etag } }),
    };
    const response = await apiClient.get(`/api/topics/${topicId}/digest`, config);
    if (response.status === 304) {
      return { status: 304, etag, payload: null };
    }
    return {
      status: 200,
      etag: response.headers['etag'] ?? null,
      payload: response.data,
    };
  } catch (error) {
    // Reason: 404 = digest not yet generated — maps to null payload.
    if (error.response?.status === 404) {
      return { status: 200, etag: null, payload: null };
    }
    throw new Error(error.response?.data?.detail || 'Failed to get topic digest');
  }
};

export const getBriefingConditional = async ({ etag, limit = 10, include_misc = true } = {}) => {
  try {
    const queryParams = new URLSearchParams({ limit, include_misc });
    const config = {
      validateStatus: s => s === 304 || (s >= 200 && s < 300),
      ...(etag && { headers: { 'If-None-Match': etag } }),
    };
    const response = await apiClient.get(`/api/topics/briefing?${queryParams}`, config);
    if (response.status === 304) {
      return { status: 304, etag, payload: null };
    }
    return {
      status: 200,
      etag: response.headers['etag'] ?? null,
      payload: response.data,
    };
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch briefing');
  }
};

/**
 * Get the current clustering status for the user
 * @returns {Promise<Object>} Clustering status (is_running, started_at, message)
 */
export const getClusteringStatus = async () => {
  try {
    const response = await apiClient.get('/api/topics/clustering-status');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get clustering status'
    );
  }
};

/**
 * Start asynchronous topic regeneration in the background
 * @returns {Promise<Object>} Response with success status and message
 */
export const regenerateTopics = async () => {
  try {
    const response = await apiClient.post('/api/topics/regenerate');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to start topic regeneration'
    );
  }
};